import cx from "classnames";
import { memo, RefObject, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import ListEntitiesItemTitle from "components/ListEntitiesItem/Title";
import { Spinner } from "components/Spinner";
import { ChevronRight } from "components/icons/generated";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Counter from "ds/components/Counter";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";
import { TreeNode } from "utils/tree";
import { withTestId } from "utils/withTestId";

import {
  COLUMN_GAP,
  COLUMN_ORDER,
  CONFIG_MANAGEMENT_TREE_LIST_ROW_TEST_ID,
  LEVEL_MARGIN,
} from "./constants";
import styles from "./styles.module.css";
import { ConfigNode } from "../../types";
import { ANSIBLE_STATUS_BADGE_VARIANT } from "../constants";
import { TASK_SEARCH_KEY } from "../../constants";

type ConfigManagementTreeGridListRowProps = {
  node: TreeNode<ConfigNode>;
  isActive: boolean;
  isExpanded: boolean;
  searchQuery?: string;
  onToggle: () => void;
  loading?: boolean;
  hasChildrenToLoad?: boolean;
  innerRef?: RefObject<HTMLDivElement | null>;
  description?: string;
  analyticsPage?: AnalyticsPage;
  setRowHeight?: (size: number) => void;
};

const ConfigManagementTreeGridListRow = ({
  isActive,
  isExpanded,
  searchQuery,
  onToggle,
  node,
  hasChildrenToLoad,
  loading,
  description,
  analyticsPage,
  setRowHeight,
}: ConfigManagementTreeGridListRowProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });
  const rowRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const newUrlParams = new URLSearchParams(location.search);
  newUrlParams.set(TASK_SEARCH_KEY, node.id);

  const hasChildren = (node.children && node.children.length > 0) || hasChildrenToLoad;
  const rowLevel = node.path.length;
  const expandable = hasChildren || !!node.item.limitMargin ? "0px" : LEVEL_MARGIN;
  const marginLeft = `calc(${LEVEL_MARGIN} * ${rowLevel - 1} + ${expandable})`;

  const linkOnClickCallback = useCallback(
    () => trackSegmentAnalyticsEvent("Drawer opened"),
    [trackSegmentAnalyticsEvent]
  );

  const handleRowHeight = useCallback(() => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  }, [setRowHeight]);

  useEffect(handleRowHeight);

  return (
    <ListEntitiesItem
      key={node.id}
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      isActive={isActive}
      {...withTestId(CONFIG_MANAGEMENT_TREE_LIST_ROW_TEST_ID)}
      ref={rowRef}
      role="row"
      aria-expanded={isExpanded}
      aria-level={rowLevel}
    >
      <Box
        gap="medium"
        align="center"
        style={{
          marginLeft,
        }}
        className={cx({
          [styles.ordered]: node.item.order,
          [styles.first]: node.item.order === 1,
          [styles.last]: node.item.last,
          [styles.hasChildrenOpen]: isExpanded && hasChildren,
          [styles.hasParentTask]: node.item.hasMiddleGroup,
          [styles.lastUnderRole]: node.item.lastUnderRole,
        })}
        role="rowheader"
      >
        {node.item.order && (
          <Box margin="0 medium 0 0" className={styles.order}>
            <Counter count={node.item.order} />
          </Box>
        )}
        {hasChildren && (
          <Box shrink="0" padding={loading ? "small" : "0"}>
            {loading ? (
              <Icon src={Spinner} />
            ) : (
              <ButtonIcon
                icon={ChevronRight}
                iconRotate={isExpanded ? "90" : undefined}
                onPress={onToggle}
                variant="ghost"
              >
                {isExpanded ? "Collapse" : "Expand"}
              </ButtonIcon>
            )}
          </Box>
        )}
        <Box>
          <Box direction="column" gap="small" fullWidth>
            {!node.item.link ? (
              <ListEntitiesItemTitle title={node.name} />
            ) : (
              <ListEntitiesItemLink
                title={node.name}
                href={`${node.item.link?.path}?${newUrlParams}`}
                searchQuery={searchQuery}
                onClick={linkOnClickCallback}
              />
            )}
            {description && (
              <Typography tag="span" variant="p-body3" color="secondary">
                {description}
              </Typography>
            )}
          </Box>
        </Box>

        {node.item.count && <Counter count={node.item.count} />}
      </Box>
      <Box direction="row" gap="medium" align="center" shrink="0" role="gridcell">
        {node.item.status && (
          <Badge
            variant={ANSIBLE_STATUS_BADGE_VARIANT[node.item.status]}
            type="strong"
            text={node.item.status}
          />
        )}

        {node.item.checkMode && <Badge variant="gray" type="strong" text="CHECK MODE" />}
      </Box>
    </ListEntitiesItem>
  );
};

export default memo(ConfigManagementTreeGridListRow);
